
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import { fromFields, fromFieldTitles } from './constants';
import WarRoomEditProfileFormSalesRoles from '@/components/partials/war-room/WarRoomEditProfile/WarRoomEditProfileForm/WarRoomEditProfileFormSalesRoles.vue';
import { companyStages as companySeriesStagesList } from '@/components/partials/jobs/constants/selectableOptions';
import JobsDropdown from '@/components/partials/jobs/JobsDropdown/JobsDropdown.vue';
import type { SelectableOption } from '@/components/partials/jobs/onboarding/types';
import PortfolioAutofocusFormFieldMixin from '@/components/partials/portfolio/PortfolioAutofocusFormFieldMixin';
import JobsCompanySeriesStagesQuery from '@/apollo/queries/Jobs/JobsCompanySeriesStages.query';
import BravadoInlineCheckbox from '~/components/common/BravadoInlineCheckbox/BravadoInlineCheckbox.vue';
import type {
  CompanyStage,
  Department,
  Industry,
  CurrentUser,
  SalesRole,
  CompanySeriesStage,
  ProductType,
  CompanySeriesStageSlug,
  PublicPortfolioConfiguration,
} from '~/types/graphql';
import WarRoomOnboardingDataQuery from '~/apollo/queries/WarRoomOnboarding.query';
import WarRoomEditProfileFormProductTypes from '~/components/partials/war-room/WarRoomEditProfile/WarRoomEditProfileForm/WarRoomEditProfileFormProductTypes.vue';
import BravadoCallout from '~/components/common/BravadoCallout/BravadoCallout.vue';
import BravadoToggle from '~/components/common/BravadoToggle/BravadoToggle.vue';

interface IForm {
  [fromFields.SECTION_SALES_EXPERIENCE_IS_PUBLIC]: boolean;
  [fromFields.DEPARTMENTS]: string[] | undefined;
  [fromFields.PRODUCT_TYPES]: ProductType[] | undefined;
  [fromFields.INDUSTRIES]: string[] | undefined;
  [fromFields.COMPANY_STAGES]: string[] | undefined;
  [fromFields.WAR_ROOM_SALES_ROLES]: SalesRole[] | undefined;
  [fromFields.COMPANY_SERIES_STAGES]: string[] | undefined;
}

interface IOnboardingData {
  departments: Department[];
  industries: Industry[];
  companyStages: CompanyStage[];
}

interface IData {
  form: IForm;
  attemptedSubmission: boolean;
  onboardingData: IOnboardingData | undefined;
  fromFields: Record<
    string,
    string | undefined | Department[] | ProductType[] | Industry[] | CompanyStage[] | SalesRole[] | CompanySeriesStage[]
  >;
  companySeriesStagesOptions: SelectableOption[];
  jobsCompanySeriesStages: CompanySeriesStage[] | null;
}

export default Vue.extend({
  name: 'PortfolioEditSalesExperienceModalForm',
  components: {
    BravadoInlineCheckbox,
    WarRoomEditProfileFormProductTypes,
    WarRoomEditProfileFormSalesRoles,
    JobsDropdown,
    BravadoCallout,
    BravadoToggle,
  },
  apollo: {
    onboardingData: {
      query: WarRoomOnboardingDataQuery,
    },
    jobsCompanySeriesStages: {
      query: JobsCompanySeriesStagesQuery,
      error(e) {
        this.$notice.error(e);
      },
    },
  },
  mixins: [validationMixin, PortfolioAutofocusFormFieldMixin],
  validations: {
    form: {
      [fromFields.DEPARTMENTS]: {
        required,
      },
      [fromFields.INDUSTRIES]: {
        required,
      },
      [fromFields.COMPANY_STAGES]: {
        required,
      },
      [fromFields.WAR_ROOM_SALES_ROLES]: {
        required,
      },
    },
  },
  props: {
    user: {
      type: Object,
      required: true,
    } as PropOptions<CurrentUser>,
    configuration: {
      type: Object,
      required: true,
    } as PropOptions<PublicPortfolioConfiguration>,
  },
  data(): IData {
    const departments = this.user.departments?.map((item: Department) => item.id);
    const industries = this.user.industries?.map((item: Industry) => item.id);
    const companyStages = this.user.companyStages?.map((item: CompanyStage) => item.id);
    const companySeriesStages = this.user.companySeriesStages?.map(item => item.slug);

    const companySeriesStagesOptions = companySeriesStagesList.map(action => ({
      ...action,
      selected: companySeriesStages?.includes(action.key as CompanySeriesStageSlug),
    }));

    return {
      form: {
        [fromFields.SECTION_SALES_EXPERIENCE_IS_PUBLIC]: this.configuration.sectionSalesExperienceIsPublic,
        [fromFields.DEPARTMENTS]: departments,
        [fromFields.PRODUCT_TYPES]: this.user.productTypes ? [...this.user.productTypes] : undefined,
        [fromFields.INDUSTRIES]: industries,
        [fromFields.COMPANY_STAGES]: companyStages,
        [fromFields.WAR_ROOM_SALES_ROLES]: this.user.warRoomSalesRoles ? [...this.user.warRoomSalesRoles] : undefined,
        [fromFields.COMPANY_SERIES_STAGES]: companySeriesStages,
      },
      attemptedSubmission: false,
      onboardingData: {
        departments: [],
        industries: [],
        companyStages: [],
      },
      fromFields,
      companySeriesStagesOptions,
      jobsCompanySeriesStages: null,
    };
  },
  computed: {
    isFieldValid(): Record<string, boolean> {
      return Object.keys(this.$v.form).reduce((acc, key) => {
        return { ...acc, [key]: this.checkRequiredField(key) };
      }, {});
    },
    fieldErrorMessage(): Record<string, string> {
      return Object.keys(this.$v.form).reduce((acc: Record<string, string>, key) => {
        acc[key] = this.getRequiredFieldErrorMessage(key, fromFieldTitles[key]);

        return acc;
      }, {});
    },
    companySeriesStageTitle(): string | null {
      const field = this.form[fromFields.COMPANY_SERIES_STAGES];
      if (!field?.length) return null;

      const selected = field?.map(stageId => {
        const option = companySeriesStagesList.find(cs => cs.key === stageId);
        return option?.title;
      });

      return selected.join(', ');
    },
  },
  methods: {
    handleDepartmentChange($event: Array<string>): void {
      this.form.departments = $event.map(id => id);
    },
    checkRequiredField(fieldName: string): boolean {
      if (!this.$v.form?.$dirty) return true;

      const field = this.$v.form[fieldName];

      return !field?.$invalid;
    },
    getRequiredFieldErrorMessage(fieldName: string, fieldTitle: string): string {
      if (!this.$v.form?.$dirty) return '';

      const field = this.$v.form[fieldName];

      if (!field?.required) {
        return `Please provide ${fieldTitle}`;
      }

      return '';
    },
    save(): void {
      this.attemptedSubmission = true;
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      const userAttrs = {
        departmentIds: this.form.departments?.map(item => Number(item)),
        productTypeIds: this.form.productTypes?.map(item => Number(item.id)),
        warRoomIndustryIds: this.form.industries?.map(item => Number(item)),
        warRoomCompanyStageIds: this.form.companyStages?.map(item => Number(item)),
        warRoomSalesRoleIds: this.form.warRoomSalesRoles?.map(item => Number(item.id)),
        warRoomCompanySeriesStageIds: this.form.companySeriesStages?.map(key => {
          const option = this.jobsCompanySeriesStages?.find(cs => cs.slug === key);
          return Number(option?.id);
        }),
      };

      const configurationAttrs = {
        sectionSalesExperienceIsPublic: this.form.sectionSalesExperienceIsPublic,
      };

      this.$emit('upsert', { userAttrs, configurationAttrs });
    },
  },
});
